import React from 'react';

import { arrayOf, calcValue, isDefined, mapDefined, padStringToFixed } from '@nfctron/nexus-core';
import { UiButton, useContextRequired, useFormat, useIsMounted } from '@nfctron/nexus-react';

import { Text, Title } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { ClockIcon, RefreshCcwIcon } from 'lucide-react';
import Image from 'next/image';
import Countdown from 'react-countdown';

import { useVisualEffects } from '@root/features/layout/components/VisualEffects/VisualEffects';

import { getUseEntryQueueKey } from '@root/cart/_resources/hooks/useEntryQueue';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useNow } from '@root/lib/hooks/useNow';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../components/CartContext';
import { getCartTicketSaleState, useCartStore, useTicketTypesApiQuery } from '../stores/cartStore';

/**
 * Event/Cart/NoTicketsAvailable atom component
 * ✅ 1. saleFrom/saleTo is null - really no tickets
 * ✅ 2. saleFrom is in the future - tickets not yet on sale
 * ✅ 3. saleTo is in the past - tickets are sold out
 * ✅ 4. invalid event configuration
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const NoTicketsAvailableAtom: AtomComponent = () => {
	const now = useNow(1000);
	const format = useFormat();
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, NoTicketsAvailableAtom.displayName);
	const isMounted = useIsMounted();
	const { event } = useContextRequired(EventCartPageContext);
	const cartConfig = useCartStore((state) => state.cartConfig);
	const ticketTypesApiQuery = useTicketTypesApiQuery();

	/** detect the state */
	const saleStatus = calcValue(() => {
		const cartTicketSaleState = getCartTicketSaleState({
			countdownDate: mapDefined(event.extraData.countdownDate, (d) => new Date(d)) ?? null,
			saleFrom: mapDefined(event.saleInfo?.saleFrom, (d) => new Date(d)) ?? null,
			saleTo: mapDefined(event.saleInfo?.saleTo, (d) => new Date(d)) ?? null,
			ticketsSet: event.saleInfo?.ticketsSet ?? false,
			hasPaymentMethods: event.paymentMethods.length > 0,
		});
		if (cartTicketSaleState.countdownStatus === 'active') return 'notYetOnSale';
		switch (cartTicketSaleState.saleStatus) {
			case 'not-started': {
				return 'notYetOnSale';
			}
			case 'ended': {
				return 'soldOut';
			}
			case 'active': {
				return 'noTickets';
			}
			default: {
				return 'invalidConfig';
			}
		}
	});

	const queryClient = useQueryClient();
	const confetti = useVisualEffects();

	return (
		<div className="flex grow flex-col items-center justify-center text-center">
			{
				{
					/* 1. saleFrom/saleTo is null - really no tickets */
					noTickets: (
						<div className="m-auto flex flex-col gap-5">
							<Image className="mx-auto" src="/static/images/astro-rocket.svg" alt={t('noTicketsSet.title')} width={300} height={300} />
							<Title size="h3" c="lucky-point" fw={700}>
								{t('noTicketsSet.title')}
							</Title>
							<Text size="md" c="dimmed">
								{t('noTicketsSet.text')}
							</Text>
						</div>
					),
					/* 2. saleFrom is in the future - tickets not yet on sale */
					notYetOnSale: (
						<React.Fragment>
							<div className="w-full p-4 text-left">
								<Title size="h3" c="lucky-point" fw="bold">
									{t('saleNotStarted.title')}
								</Title>
								<Text size="md" c="dimmed">
									{cartConfig.saleInfo.countdownDate
										? t('saleNotStarted.text', { from: format.date(new Date(cartConfig.saleInfo.countdownDate), 'PPp') })
										: t('saleNotStarted.text', {
												from: isDefined(cartConfig.saleInfo.saleFrom) ? format.date(cartConfig.saleInfo.saleFrom, 'PPp') : 'n/a',
											})}
								</Text>
							</div>

							<div className="relative flex w-full grow flex-col items-center justify-center overflow-hidden rounded-lg border border-gray-200">
								<div className="absolute flex w-full flex-col items-center justify-start gap-3 justify-self-start p-2 opacity-50 blur-xs">
									{/* dummy ticket type like cards */}
									{arrayOf(10).map((key) => (
										<div key={key} className="h-[125px] w-full rounded-lg border border-gray-400 bg-gray-200" />
									))}
								</div>
								{/* countdown / lock icon */}
								{isDefined(cartConfig.saleInfo.countdownDate) ? (
									<div className="absolute z-10 mx-8 place-items-center rounded-lg border border-gray-200 bg-white p-4">
										<div className="m-auto flex flex-col gap-3 text-center">
											{/* countdown */}
											<Countdown
												date={cartConfig.saleInfo.countdownDate}
												/** fetch new ticket types upon countdown completion */
												onComplete={() => {
													queryClient
														.fetchQuery({
															queryKey: getUseEntryQueueKey(cartConfig.eventId, event.organizer.slug),
															staleTime: 0,
														})
														.then(async (data: any) => {
															/** do not shoot confetti if the user should be queued */
															if (data?.shouldQueue) return;
															/** shoot confetti and load ticket types */
															confetti.confettiShootDefault();
															await ticketTypesApiQuery.refetch();
														});
												}}
												renderer={({ hours, minutes, seconds, api, days, props }) => {
													if (!isMounted) return null;
													if (api.isCompleted()) {
														return (
															<React.Fragment>
																{/* reload button */}
																<UiButton
																	fullWidth
																	leftSection={<RefreshCcwIcon />}
																	loading={ticketTypesApiQuery.isLoading}
																	onClick={() => ticketTypesApiQuery.refetch()}
																	variant="secondary"
																	size="lg"
																>
																	{t('noTickets.buttonLabel')}
																</UiButton>
															</React.Fragment>
														);
													}

													return (
														<div className="mt-1 flex flex-col items-center justify-center rounded-lg border border-zinc-100 bg-zinc-50 px-4 py-8">
															{/* content */}
															<div className="flex flex-col gap-5">
																{/* labels */}
																<div className="text-md flex items-center justify-center gap-1 text-center leading-none font-medium text-zinc-500 uppercase">
																	<span className="w-20">{t('countdown.daysLabel')}</span>
																	<span className="w-20">{t('countdown.hoursLabel')}</span>
																	<span className="w-20">{t('countdown.minutesLabel')}</span>
																	<span className="w-20">{t('countdown.secondsLabel')}</span>
																</div>
																{/* values */}
																<div className="flex items-center justify-center">
																	{[days, hours, minutes, seconds].map((value, index, arr) => (
																		<React.Fragment key={index}>
																			<div className="w-20 text-center text-[2.5rem] font-bold text-zinc-700">
																				{padStringToFixed(value.toString(), 2, '0')}
																			</div>
																			{index !== arr.length - 1 && <span className="font-bold text-zinc-400">:</span>}
																		</React.Fragment>
																	))}
																</div>
															</div>
														</div>
													);
												}}
											/>
										</div>
									</div>
								) : (
									<span className="absolute z-10 grid size-16 place-items-center rounded-md border border-gray-200 bg-white text-4xl text-gray-500">
										<ClockIcon />
									</span>
								)}
							</div>
						</React.Fragment>
					),
					/* 3. saleTo is in the past - tickets are sold out */
					soldOut: (
						<div className="m-auto flex flex-col gap-5">
							<Image className="mx-auto" src="/static/images/astro-rocket.svg" alt={t('saleEnded.title')} width={300} height={300} />
							<Title size="h3" c="lucky-point" fw={700}>
								{t('saleEnded.title')}
							</Title>
							<Text size="md" c="dimmed">
								{t('saleEnded.text')}
							</Text>
						</div>
					),
					/* 4. invalid event configuration */
					invalidConfig: (
						<div className="m-auto flex flex-col gap-5">
							<Image className="mx-auto" src="/static/images/astro-rocket.svg" alt={t('noTicketsSet.title')} width={300} height={300} />
							<Title size="h3" c="lucky-point" fw={700}>
								{t('noTicketsSet.title')}
							</Title>
							<Text size="md" c="dimmed">
								{t('noTicketsSet.text')}
							</Text>
						</div>
					),
				}[saleStatus]
			}
		</div>
	);
};

/**
 * Event/Cart/NoTicketsAvailable atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
NoTicketsAvailableAtom.displayName = 'Event/Cart/NoTicketsAvailableAtom';
